import type { DnsechoRow, IpDetails } from '@shared/api'

/**
 * The single definition of "an operator" for the whole results screen.
 *
 * The product exists to answer one number -- how many distinct parties saw your
 * DNS queries -- so that number has to be computed one way. It was not: the
 * verdict counted only resolvers it could name (dropping any with no ASN and no
 * ISP), while the concentration chart keyed unnameable resolvers by their IP and
 * counted each as its own party. On the same result those two rules disagree,
 * which is exactly the number a visitor came to trust.
 *
 * The rule here is the honest one: a resolver we cannot name is still a distinct
 * party, so it counts, keyed by its address. Identity falls back
 * ASN -> operator name -> ISP -> the IP itself, and the display name follows the
 * same order.
 */

export interface Operator {
    key: string
    name: string
    /** Total observed queries across every resolver belonging to this operator. */
    count: number
    /** This operator is the visitor's own connection provider. */
    isSelf: boolean
}

type OperatorFields = Pick<DnsechoRow, 'asnNumber' | 'asnOrg' | 'isp' | 'ip'>

/** Stable identity for an operator. Every resolver resolves to exactly one. */
export function operatorKey(row: Pick<OperatorFields, 'asnNumber' | 'asnOrg' | 'isp' | 'ip'>): string {
    if (row.asnNumber !== undefined) return `AS${row.asnNumber}`
    return row.asnOrg ?? row.isp ?? row.ip
}

/** Human-readable name, following the same fallback order as the key. */
export function operatorName(row: Pick<OperatorFields, 'asnOrg' | 'isp' | 'ip'>): string {
    return row.asnOrg ?? row.isp ?? row.ip
}

/** The visitor's own key, or undefined when their ASN/ISP is unknown -- in which
    case there is nothing to compare resolvers against. `ip` is not used here:
    the connection's own address is not one of its resolvers. */
export function selfOperatorKey(self: IpDetails | undefined): string | undefined {
    if (!self) return undefined
    if (self.asnNumber !== undefined) return `AS${self.asnNumber}`
    return self.asnOrg ?? self.isp
}

/**
 * Collapses resolver rows into distinct operators, largest share first.
 *
 * This is the canonical list. `organisations` in the verdict is its length; the
 * concentration chart draws from it directly. Nothing else may count operators.
 */
export function toOperators(rows: DnsechoRow[], self: IpDetails | undefined): Operator[] {
    const selfKey = selfOperatorKey(self)
    const byKey = new Map<string, Operator>()

    for (const row of rows) {
        const key = operatorKey(row)
        const existing = byKey.get(key)
        if (existing) {
            existing.count += row.count
        } else {
            byKey.set(key, { key, name: operatorName(row), count: row.count, isSelf: key === selfKey })
        }
    }

    return [...byKey.values()].sort((a, b) => b.count - a.count)
}
